import React from 'react';
import { Link } from 'react-router-dom';

/**
 * nischals EduLab Monitor brand mark.
 * Renders the nischals logo + product wordmark with consistent styling.
 *
 * variant: 'full' (logo + wordmark) | 'compact' (logo only) | 'wordmark' (text only)
 * size: 'sm' | 'md' | 'lg'
 * linkTo: optional route (default '/'). Pass null to disable the link wrapper.
 */
const BrandLogo = ({
  variant = 'full',
  size = 'md',
  showTagline = false,
  inverted = false,
  className = '',
  linkTo = '/',
}) => {
  const sizes = {
    sm: { logo: 'h-8 w-8', title: 'text-sm', subtitle: 'text-[10px]' },
    md: { logo: 'h-10 w-10', title: 'text-base', subtitle: 'text-xs' },
    lg: { logo: 'h-14 w-14', title: 'text-xl', subtitle: 'text-sm' },
  };
  const s = sizes[size] || sizes.md;

  const logo = (
    <div className={`${s.logo} relative flex-shrink-0`}>
      <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-amber-50 to-emerald-50 rounded-xl opacity-70" />
      <div className="relative bg-white rounded-xl flex items-center justify-center overflow-hidden border border-rose-100/60 shadow-sm h-full w-full">
        <img
          src="/nischals-logo.png"
          alt="nischals — Joy of Knowing"
          className="object-contain p-1 h-full w-full"
        />
      </div>
    </div>
  );

  if (variant === 'compact') {
    const inner = <div className={className}>{logo}</div>;
    return linkTo ? <Link to={linkTo} className="inline-block rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300" aria-label="Go to home">{inner}</Link> : inner;
  }

  const content = (
    <div className={`flex items-center gap-3 ${className}`}>
      {variant !== 'wordmark' && logo}
      <div className="leading-tight">
        <h1 className={`${s.title} font-bold tracking-tight ${inverted ? 'text-white' : 'text-slate-900'}`}>
          <span style={{ color: '#e4376f' }}>nischals</span>{' '}
          <span className={inverted ? 'text-white' : 'text-slate-800'}>EduLab Monitor</span>
        </h1>
        {showTagline && (
          <p className={`${s.subtitle} ${inverted ? 'text-white/70' : 'text-slate-500'} italic`} style={!inverted ? { color: '#e4376f', opacity: 0.75 } : {}}>
            Joy of Knowing™
          </p>
        )}
      </div>
    </div>
  );

  if (!linkTo) return content;
  return (
    <Link
      to={linkTo}
      className="inline-flex items-center rounded-xl hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-rose-300"
      aria-label="Go to home"
      data-testid="brand-logo-link"
    >
      {content}
    </Link>
  );
};

export default BrandLogo;
